import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

import { LetDirective } from '@ngrx/component';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment/moment';
import { BehaviorSubject, Observable, catchError, finalize, first, forkJoin, switchMap, tap, withLatestFrom } from 'rxjs';

import { AuthService } from '@personal-area/global/authorization/services';
import { LoadingState } from '@personal-area/global/state';
import { CountryChangeApiService, CountryChangerService } from '@personal-area/global/country-change';
import { CardWrapperModule } from '@personal-area/global/ui';
import { CountrySelectComponent } from '@personal-area/global/country';
import { ButtonComponent } from '@personal-area/global/ui';
import { FormFieldComponent, CalendarInputComponent } from '@personal-area/global/ui';
import { UserApiService, UserFacade } from '@personal-area/global/users/services';
import { I18nService } from '@personal-area/global/i18n';
import { WebsocketFallbackService } from '@personal-area/global/websocket';

import { ApiFormService, Validators } from '@personal-area/global/form-validation/services';
import { ProfileTrackingService } from '@personal-area/global/profile/services';

import { SkipData, SkipVerificationDialogComponent } from '../../../components/skip-verification-dialog/skip-verification-dialog.component';
import { SuccessSnackBarComponent, SuccessSnackBarConfig } from '../../../components/success-snack-bar/success-snack-bar.component';
import { BasicVerificationStatus, IDVerificationStatus } from '../../../models/verification-profile-response';
import { IdentityVerificationApiService } from '../../../services/identity-verification-api.service';
import { VerificationProfileApiService } from '../../../services/verification-profile-api.service';
import { SummaryVerificationFacade } from '../../../state/facades/summary';
import { SummaryState } from '../../../state/reducers/summary';

const MIN_YEARS_OLD = 18;

@Component({
  templateUrl: './verification-basic.component.html',
  styleUrls: ['./verification-basic.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CardWrapperModule,
    ButtonComponent,
    CommonModule,
    ReactiveFormsModule,
FormFieldComponent,
    CalendarInputComponent,
    TranslateModule,
    CountrySelectComponent,
    MatSnackBarModule,
    LetDirective,
  ],
})
export class VerificationBasicComponent
  implements OnInit {
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _userApiService = inject(UserApiService);
  private readonly _formService = inject(ApiFormService);
  private readonly _router = inject(Router)
  private readonly _profileTrackingService = inject(ProfileTrackingService);
  private readonly _wsFallbackService = inject(WebsocketFallbackService);
  private readonly _userStatusService = inject(IdentityVerificationApiService);
  private readonly _countryChangerService = inject(CountryChangerService);
  private readonly _userFacade = inject(UserFacade);
  private readonly _dialog = inject(MatDialog);
  private readonly _i18nService = inject(I18nService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _summaryFacade = inject(SummaryVerificationFacade);
  private readonly _verificationProfileApiService = inject(VerificationProfileApiService);
  private readonly _authService = inject(AuthService);
  private readonly _countryChangeApiService = inject(CountryChangeApiService);
  private destroyRef = inject(DestroyRef);

  public readonly minDate = new Date(1900, 0, 1);
  public readonly maxDate: Date;
  public readonly fieldErrors = {
    name: {
      availableChars: 'common.error.chars-are-not-available',
      required: 'profile-identity.error.name-required',
      maxlength: 'profile-identity.error.name-maxlength',
    },
    birthDate: {
      required: 'profile-identity.error.birthDate-required',
      matDatepickerMin: 'profile-identity.error.birthDate-invalid',
      age: 'profile-identity.error.birthDate-invalid',
    },
    country: {
      required: 'profile-identity.error.country-required',
    },
  };
  public form = this._formBuilder.nonNullable.group({
    name: [
      '',
      [
        Validators.required,
        Validators.availableChars,
        Validators.maxLength(255),
      ],
    ],
    birthDate: [
      null as null | moment.Moment,
      [Validators.required, Validators.age(MIN_YEARS_OLD)],
    ],
    country: [null as null | string, Validators.required],
  });
  public submitErrors = new EventEmitter<void>();
  public isPending$ = new BehaviorSubject(false);

  public readonly user$ = this._userFacade.isUserLoaded$.pipe(
    first(Boolean),
    switchMap(() => this._userFacade.user$),
    first(),
  );

  constructor() {
    const now = new Date();
    this.maxDate = new Date(now.setFullYear(now.getFullYear() - MIN_YEARS_OLD));
  }

  public ngOnInit(): void {
    this.user$.subscribe((user) =>
      this.form.setValue({
        name: user.name,
        birthDate: user.birthDate
          ? moment(user.birthDate || this.minDate).utcOffset(0, true)
          : null,
        country: user.country || null,
      }),
    );
  }

  public trackDateInputEvent(): void {
    this._profileTrackingService.birthDateInputChanged();
  }

  public onSubmit(): void {
    if (!this.form.valid) {
      this.submitErrors.next();
      return;
    }

    const { country, birthDate, name } = this.form.getRawValue();

    this.isPending$.next(true);
    forkJoin([
      this._verificationProfileApiService.setExpressVerification(
        this._authService.userId,
      ),
      this._userApiService.changeName(this._authService.userId, name),
      this._userStatusService.updateBirthDate(birthDate?.toISOString() ?? ''),
      this._countryChangeApiService.changeCountry({
        country: country ?? '',
        files: [],
      }),
    ]).pipe(
      catchError((error) => this._separateErrors(error)),
      finalize(() => this.isPending$.next(false)),
      withLatestFrom(this.user$),
      takeUntilDestroyed(this.destroyRef)
    )
      .subscribe({
        next: async ([_, user]) => {
          this._wsFallbackService.reloadUser();
          this._countryChangerService.refreshStatus();

          if (user.isEmailConfirmed) {
            this._summaryFacade.mergeStatus({
              basicStatus: BasicVerificationStatus.trial,
            });
            await this._router.navigate(['/dashboard']);
            this._openSuccessSnackBar();
          } else {
            this._summaryFacade.mergeStatus({
              basicStatus: BasicVerificationStatus.basicFormPassed,
            });
            await this._router.navigate(['/profile/verification/express/email']);
          }
        },
        error: async (err: HttpErrorResponse | SummaryState) => {
          if (err instanceof HttpErrorResponse) {
            this._formService.defaultFormError(err, this.form);
            this.submitErrors.next();
            return;
          }

          await this._resolveVerificationConflict(err);
        },
      });
  }

  private _openSuccessSnackBar(): void {
    this._snackBar.openFromComponent(SuccessSnackBarComponent, <MatSnackBarConfig<SuccessSnackBarConfig>>{
      data: {
        text: 'verification.basic.success.snack-bar.text',
      },
      direction: this._i18nService.isRtl ? 'rtl' : 'ltr',
      verticalPosition: 'top',
      horizontalPosition: 'center',
      duration: 5000,
    });
  }

  public promptSkip(): void {
    this._dialog.open(SkipVerificationDialogComponent, <MatDialogConfig<SkipData>>{
      panelClass: 'regular-popup-container',
      direction: this._i18nService.isRtl ? 'rtl' : 'ltr',
      data: {
        text: 'verification-modal.text.skip.route',
      },
    });
  }

  private _separateErrors(err: HttpErrorResponse): Observable<SummaryState> {
    const errorCode: number = err.error.exception?.code || err.error.mapException?.code;
    const alreadyVerifiedErrorCodes = [342004, 11000];

    if (!alreadyVerifiedErrorCodes.includes(errorCode)) {
      throw err;
    }

    this._summaryFacade.getSummary();
    return this._summaryFacade.summary$.pipe(
      tap((summary) => {
        throw summary;
      }),
    );
  }

  // React to ID verification update, prohibiting to submit basic verification
  private async _resolveVerificationConflict(
    state: SummaryState,
  ): Promise<void> {
    if (state.loadingState === LoadingState.failed) {
      this._formService.openErrorPopup();
    } else {
      switch (state.idStatus) {
        case IDVerificationStatus.banned:
          await this._router.navigateByUrl('profile/verification/identity/banned');
          break;
        case IDVerificationStatus.accepted:
          await this._router.navigateByUrl('dashboard');
          break;
        default:
          this._formService.openErrorPopup();
      }
    }
  }
}
